// *******************************************************************************
// File upload
// *******************************************************************************

const filesToUpload = [];

const SIZE_ATTRIBUTE_NAMES = [
  'data-fv-file___min-size',
  'data-fv-file___max-size',
  'data-fv-file___min-total-size',
  'data-fv-file___max-total-size',
];

const convertUploadInputSizeAttributes = (input) => {
  SIZE_ATTRIBUTE_NAMES.forEach((attr) => {
    const value = input.getAttribute(attr);

    if (!value) return;

    const sizeInBytes = parseFloat(String(value).replace(',', '.')) * 1024 * 1024;

    if (Number.isFinite(sizeInBytes)) {
      input.setAttribute(attr, String(Math.round(sizeInBytes)));
    }
  });
};

const initUploadBoxInputs = () => {
  const fileUploadInput = document.querySelectorAll('.upload-box__input');

  fileUploadInput.forEach(convertUploadInputSizeAttributes);

  return fileUploadInput;
};

const fileUploadInput = initUploadBoxInputs();

if (fileUploadInput.length > 0) {
  let nextFileID = 1;

  /* ---------------- TOAST ---------------- */

  const getToastContainer = () => {
    let toastContainer = document.querySelector('.toast-container');

    if (!toastContainer) {
      toastContainer = document.createElement('div');
      toastContainer.className = 'toast-container';
      toastContainer.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastContainer);
    }

    return toastContainer;
  };

  const showToast = (message, type = 'default') => {
    const toastContainer = getToastContainer();
    const toast = document.createElement('div');
    toast.className = `toast-container-item toast-container-item--${type}`;
    toast.textContent = message;

    toastContainer.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('toast-hide');

      const onAnimationEnd = (e) => {
        if (e.target !== toast || e.animationName !== 'toast-out') return;
        toast.removeEventListener('animationend', onAnimationEnd);
        setTimeout(() => toast.remove(), 200);
      };

      toast.addEventListener('animationend', onAnimationEnd);
    }, 3000);
  };

  const getMaxFiles = (parentElem, input) => {
    const fromInput = Number(input?.getAttribute('data-max-files'));
    if (Number.isFinite(fromInput) && fromInput > 0) {
      return fromInput;
    }

    return parentElem.querySelectorAll('.upload-box__item').length || 5;
  };

  const getBoxFiles = (uploadKey) => filesToUpload.filter((item) => item.parentId === uploadKey);

  const getBoxTotalSize = (uploadKey) =>
    getBoxFiles(uploadKey).reduce((sum, entry) => sum + (entry.file?.size || 0), 0);

  const getUploadSizeLimits = (input) => {
    const parseSize = (value) => {
      if (!value) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    return {
      perFileMinSize: parseSize(input.getAttribute('data-fv-file___min-size')),
      perFileMaxSize: parseSize(input.getAttribute('data-fv-file___max-size')),
      minTotalSize: parseSize(input.getAttribute('data-fv-file___min-total-size')),
      maxTotalSize: parseSize(input.getAttribute('data-fv-file___max-total-size')),
      singleMinFileSizeMessage: input.getAttribute('data-fv-file___min-size___message') || 'Datoteka je premajhna.',
      singleMaxFileSizeMessage:
        input.getAttribute('data-fv-file___max-size___message') || 'Datoteka je prevelika.',
      minTotalSizeMessage:
        input.getAttribute('data-fv-file___min-total-size___message') || 'Skupna velikost datotek je premajhna.',
      maxTotalSizeMessage:
        input.getAttribute('data-fv-file___max-total-size___message') ||
        'Skupna velikost datotek presega dovoljeno mejo.',
    };
  };

  const getUploadBoxValidationMessage = (input, fileEntries) => {
    const limits = getUploadSizeLimits(input);
    let totalSize = 0;

    for (const { file } of fileEntries) {
      if (limits.perFileMaxSize && file.size > limits.perFileMaxSize) {
        return `"${file.name}" ${limits.singleMaxFileSizeMessage}`;
      }

      if (limits.perFileMinSize && file.size < limits.perFileMinSize) {
        return `"${file.name}" ${limits.singleMinFileSizeMessage}`;
      }

      totalSize += file.size;
    }

    if (limits.maxTotalSize && totalSize > limits.maxTotalSize) {
      return limits.maxTotalSizeMessage;
    }

    if (limits.minTotalSize && totalSize < limits.minTotalSize) {
      return limits.minTotalSizeMessage;
    }

    return null;
  };

  const revokePreview = (fileEntry) => {
    if (fileEntry?.previewUrl) {
      URL.revokeObjectURL(fileEntry.previewUrl);
      fileEntry.previewUrl = null;
    }
  };

  const getPreviewUrl = (fileEntry) => {
    if (fileEntry.previewUrl) {
      return fileEntry.previewUrl;
    }

    if (fileEntry.file?.type?.startsWith('image/')) {
      fileEntry.previewUrl = URL.createObjectURL(fileEntry.file);
    }

    return fileEntry.previewUrl || '';
  };

  const getFileIconModifier = (file) => {
    const name = file.name.toLowerCase();

    if (file.type === 'application/pdf' || name.endsWith('.pdf')) {
      return 'pdf';
    }

    if (
      file.type === 'application/zip' ||
      file.type === 'application/x-zip-compressed' ||
      /\.(zip|rar|7z|tar|gz)$/i.test(name)
    ) {
      return 'zip';
    }

    return null;
  };

  const clearItemInnerFileDisplay = (itemInner) => {
    itemInner.style.backgroundImage = '';
    itemInner.classList.remove('is-file-icon');
    itemInner.querySelector('.upload-box__item-file-icon')?.remove();
  };

  const setItemInnerFileDisplay = (itemInner, fileEntry) => {
    clearItemInnerFileDisplay(itemInner);

    const previewUrl = getPreviewUrl(fileEntry);

    if (previewUrl) {
      itemInner.style.backgroundImage = `url("${previewUrl}")`;
      return;
    }

    const iconModifier = getFileIconModifier(fileEntry.file);

    if (!iconModifier) {
      return;
    }

    itemInner.classList.add('is-file-icon');
    const icon = document.createElement('i');
    icon.className = `icon icon-${iconModifier} upload-box__item-file-icon`;
    itemInner.prepend(icon);
  };

  const syncUploadBoxUI = (parentElem, uploadKey) => {
    const inner = parentElem.querySelector('.upload-box__inner');
    const items = [...parentElem.querySelectorAll('.upload-box__item')];
    const maxFiles = items.length;
    const files = getBoxFiles(uploadKey);
    const count = files.length;

    inner?.classList.toggle('hide', count >= maxFiles);

    items.forEach((item, index) => {
      const itemInner = item.querySelector('.upload-box__item-inner');
      const isLast = index === maxFiles - 1;
      const file = files[index];

      if (isLast) {
        item.classList.toggle('hide', count < maxFiles);
      } else {
        item.classList.remove('hide');
      }

      if (!itemInner) {
        return;
      }

      if (file) {
        item.dataset.fileId = String(file.id);
        itemInner.classList.remove('hide');
        setItemInnerFileDisplay(itemInner, file);
      } else {
        delete item.dataset.fileId;
        itemInner.classList.add('hide');
        clearItemInnerFileDisplay(itemInner);
      }
    });
  };

  const fileUploader = async (evt, files) => {
    const input = evt.target;
    const parentElem = evt.target.closest('.upload-box');
    const uploadKey = parentElem.dataset.uploadKey || input.id || input.name;
    const maxFiles = getMaxFiles(parentElem, input);

    const successMessage =
      parentElem.querySelector('[data-success-upload-message]')?.getAttribute('data-success-upload-message') ||
      'Datoteka je bila uspešno naložena.';

    const maxFilesMessage =
      input.getAttribute('data-fv-file___max-files___message') || 'Doseženo je največje število datotek.';

    const limits = getUploadSizeLimits(input);
    const {
      perFileMinSize,
      perFileMaxSize,
      maxTotalSize,
      singleMinFileSizeMessage,
      singleMaxFileSizeMessage,
      maxTotalSizeMessage,
    } = limits;

    const beforeCount = getBoxFiles(uploadKey).length;
    const isSingleFileReplace = !input.multiple && beforeCount > 0;
    let availableSlots = maxFiles - beforeCount;

    if (isSingleFileReplace) {
      availableSlots = 1;
    }

    if (availableSlots <= 0) {
      showToast(maxFilesMessage, 'error');
      input.value = '';
      return;
    }

    const stagedFileIds = [];
    const incomingFiles = Array.from(files).slice(0, availableSlots);

    if (incomingFiles.length < files.length) {
      showToast(maxFilesMessage, 'error');
    }

    incomingFiles.forEach((file) => {
      if (perFileMinSize && file.size < perFileMinSize) {
        showToast(`"${file.name}" ${singleMinFileSizeMessage}`, 'error');
        return;
      }

      if (perFileMaxSize && file.size > perFileMaxSize) {
        showToast(`"${file.name}" ${singleMaxFileSizeMessage}`, 'error');
        return;
      }

      const boxTotalBeforeAdd = isSingleFileReplace ? 0 : getBoxTotalSize(uploadKey);

      if (maxTotalSize && boxTotalBeforeAdd + file.size > maxTotalSize) {
        showToast(maxTotalSizeMessage, 'error');
        return;
      }

      filesToUpload.push({
        id: nextFileID,
        parentId: uploadKey,
        file,
      });
      stagedFileIds.push(nextFileID);
      nextFileID += 1;
    });

    const revertStagedFiles = () => {
      for (let i = filesToUpload.length - 1; i >= 0; i--) {
        const item = filesToUpload[i];
        if (item.parentId === uploadKey && stagedFileIds.includes(item.id)) {
          revokePreview(item);
          filesToUpload.splice(i, 1);
        }
      }
    };

    const getFilesForValidation = () => {
      if (isSingleFileReplace && stagedFileIds.length > 0) {
        return getBoxFiles(uploadKey).filter((entry) => stagedFileIds.includes(entry.id));
      }

      return getBoxFiles(uploadKey);
    };

    const commitSingleFileReplacement = () => {
      if (!isSingleFileReplace || stagedFileIds.length === 0) {
        return;
      }

      const stagedIdSet = new Set(stagedFileIds);

      for (let i = filesToUpload.length - 1; i >= 0; i--) {
        const item = filesToUpload[i];
        if (item.parentId === uploadKey && !stagedIdSet.has(item.id)) {
          revokePreview(item);
          filesToUpload.splice(i, 1);
        }
      }
    };

    const validationMessage = getUploadBoxValidationMessage(input, getFilesForValidation());

    if (validationMessage) {
      showToast(validationMessage, 'error');
      revertStagedFiles();
      input.value = '';
      syncUploadBoxUI(parentElem, uploadKey);
      return;
    }

    // eslint-disable-next-line no-promise-executor-return
    const waitForValidation = () => new Promise((resolve) => setTimeout(resolve, 0));
    await waitForValidation();

    if (parentElem.classList.contains('is-validation-failed')) {
      const asyncValidationMessage = getUploadBoxValidationMessage(input, getFilesForValidation());

      if (asyncValidationMessage) {
        showToast(asyncValidationMessage, 'error');
      }

      revertStagedFiles();
      input.value = '';
      syncUploadBoxUI(parentElem, uploadKey);
      return;
    }

    commitSingleFileReplacement();
    syncUploadBoxUI(parentElem, uploadKey);

    const afterCount = getBoxFiles(uploadKey).length;

    if (afterCount > beforeCount) {
      showToast(successMessage);
    }

    input.value = '';
  };

  attachEvent('.upload-box__input', 'change', (evt) => {
    fileUploader(evt, evt.target.files);
  });

  attachEvent('.upload-box__item-remove', 'click', (ev) => {
    ev.preventDefault();

    const button = ev.target.closest('.upload-box__item-remove');
    const item = button?.closest('.upload-box__item');
    const parentElem = button?.closest('.upload-box');

    if (!parentElem || !item) return;

    const uploadKey = parentElem.dataset.uploadKey || parentElem.querySelector('.upload-box__input')?.id;
    const fileId = Number(item.dataset.fileId);

    if (!fileId) return;

    const fileIndex = filesToUpload.findIndex((entry) => entry.id === fileId && entry.parentId === uploadKey);

    if (fileIndex === -1) return;

    const [removedFile] = filesToUpload.splice(fileIndex, 1);
    revokePreview(removedFile);

    const input = parentElem.querySelector('.upload-box__input');
    if (input && !input.multiple) {
      input.value = '';
    }

    syncUploadBoxUI(parentElem, uploadKey);
  });
}
